// stores/parrain.store.ts
import {
    DestroyRef,
    Injectable,
    computed,
    inject,
    signal,
} from '@angular/core';

import Parrain from 'config/parrain';
import Partenaire from 'app/components/partenaire/partenaire.model';
import { PartenaireMapperService } from 'config/partenaire-mapper.service';
import { SponsorService } from '../sponsor-section/sponsor.service';
import { parrainConfig } from 'config/parrain.config';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
    providedIn: 'root',
})
export class ParrainStore {
    // Signal pour les parrains - commencer avec un tableau vide
    private parrains = signal<Parrain[]>([]);
    private isLoading = signal<boolean>(false);
    private error = signal<string | null>(null);
    private destroyRef = inject(DestroyRef);

    // Computed Signals
    readonly getAllParrains = computed(() => this.parrains());

    // Récupérer tous les sponsors principaux (maximum 5)
    readonly getMainSponsors = computed(() =>
        this.parrains()
            .filter((p) => p.isMainSponsor)
            .slice(0, 5)
    );

    // Récupérer le premier sponsor principal (pour compatibilité)
    readonly getMainSponsor = computed(() => this.getMainSponsors()[0] || null);

    // Vérifier s'il y a plusieurs sponsors principaux
    readonly hasMultipleMainSponsors = computed(
        () => this.getMainSponsors().length > 1
    );

    // Compter le nombre de sponsors principaux
    readonly mainSponsorsCount = computed(() => this.getMainSponsors().length);

    readonly getLoading = computed(() => this.isLoading());
    readonly getError = computed(() => this.error());

    constructor(
        private partenaireMapper: PartenaireMapperService,
        private sponsorService: SponsorService
    ) {
        // Charger les parrains principaux au démarrage
        this.loadMainSponsors();
    }

    // Trouver un parrain par ID
    getParrainById(id: string): Parrain | undefined {
        return this.parrains().find((p) => p.id === id);
    }

    // Trouver un sponsor principal par ID
    getMainSponsorById(id: string): Parrain | undefined {
        return this.getMainSponsors().find((p) => p.id === id);
    }

    // Charger les parrains principaux depuis l'API
    loadMainSponsors(): void {
        this.isLoading.set(true);
        this.error.set(null);

        this.sponsorService
            .getMainSponsors()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (partenaires) => {
                    this.updateFromPartenaires(partenaires);
                    this.isLoading.set(false);
                },
                error: (err) => {
                    console.error(
                        'Erreur lors du chargement des sponsors principaux',
                        err
                    );
                    this.error.set(
                        'Impossible de charger les sponsors principaux'
                    );
                    this.isLoading.set(false);
                },
            });
    }

    // Charger les parrains pour un club spécifique
    loadParrainsForClub(clubId: string): void {
        this.isLoading.set(true);
        this.error.set(null);

        this.sponsorService
            .getSponsorsByClub(clubId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (partenaires) => {
                    this.updateFromPartenaires(partenaires);
                    this.isLoading.set(false);
                },
                error: (err) => {
                    console.error(
                        `Erreur lors du chargement des sponsors pour le club ${clubId}`,
                        err
                    );
                    this.error.set(
                        `Impossible de charger les sponsors pour ce club`
                    );
                    this.isLoading.set(false);
                },
            });
    }

    // Mettre à jour la liste des parrains à partir des partenaires du back
    updateFromPartenaires(partenaires: Partenaire[]): void {
        // Utiliser update() au lieu de set() avec une fonction
        this.parrains.update(() => {
            const mainSponsors = partenaires.filter((p) => p.is_main_sponsor);

            if (mainSponsors.length > 0) {
                const mappedParrains = mainSponsors
                    .slice(0, 5) // Limiter à 5 sponsors principaux
                    .map((p) => this.partenaireMapper.mapToParrain(p));

                return mappedParrains;
            } else {
                const mappedParrains = partenaires
                    .slice(0, 10)
                    .map((p) => this.partenaireMapper.mapToParrain(p));

                console.log(
                    '⚠️ No main sponsors found, using all partenaires:',
                    mappedParrains.length
                );
                return mappedParrains;
            }
        });
    }

    // Promouvoir un sponsor en sponsor principal avec notification
    promoteToMainSponsor(parrainId: string): void {

        this.parrains.update((currentParrains) => {
            const existingIndex = currentParrains.findIndex(
                (p) => p.id === parrainId
            );

            if (existingIndex >= 0) {
                const currentMainSponsorsCount = currentParrains.filter(
                    (p) => p.isMainSponsor
                ).length;

                if (currentMainSponsorsCount < 5) {
                    const updatedParrains = [...currentParrains];
                    updatedParrains[existingIndex] = {
                        ...updatedParrains[existingIndex],
                        isMainSponsor: true,
                    };


                    return updatedParrains;
                } else {
                    console.warn(
                        '❌ Cannot promote: 5 main sponsors limit reached'
                    );
                }
            } else {
                console.warn('❌ Sponsor not found:', parrainId);
            }

            return currentParrains;
        });
    }

    // Rétrograder un sponsor principal avec notification
    demoteFromMainSponsor(parrainId: string): void {

        this.parrains.update((currentParrains) => {
            const existingIndex = currentParrains.findIndex(
                (p) => p.id === parrainId
            );

            if (existingIndex >= 0) {
                const updatedParrains = [...currentParrains];
                updatedParrains[existingIndex] = {
                    ...updatedParrains[existingIndex],
                    isMainSponsor: false,
                };

                return updatedParrains;
            } else {
                console.warn('❌ Sponsor not found:', parrainId);
            }

            return currentParrains;
        });
    }

    // Réorganiser l'ordre des sponsors principaux
    reorderMainSponsors(newOrder: string[]): void {
        this.parrains.update((currentParrains) => {
            const mainSponsors = currentParrains.filter((p) => p.isMainSponsor);
            const otherSponsors = currentParrains.filter(
                (p) => !p.isMainSponsor
            );

            const reorderedMainSponsors = newOrder
                .map((id) => mainSponsors.find((p) => p.id === id))
                .filter(Boolean) as Parrain[];

            return [...reorderedMainSponsors, ...otherSponsors];
        });
    }

    // Ajouter un nouveau sponsor depuis le formulaire
    addSponsorFromForm(partenaireData: Partial<Partenaire>): void {


        // Mapper les données du formulaire vers un objet Partenaire complet
        const newPartenaire: Partenaire = {
            id:
                partenaireData.id ||
                crypto.randomUUID?.() ||
                Date.now().toString(),
            name: partenaireData.name || '',
            email: partenaireData.email || '',
            phone_number: partenaireData.phone_number || '',
            contact_name: partenaireData.contact_name || '',
            street: partenaireData.street || '',
            zipcode: partenaireData.zipcode || '',
            city: partenaireData.city || '',
            country: partenaireData.country || 'France',
            website_url: partenaireData.website_url || '',
            logo_sponsor: partenaireData.logo_sponsor || '',
            banner_sponsor: partenaireData.banner_sponsor || '',
            description: partenaireData.description || '',
            is_main_sponsor: partenaireData.is_main_sponsor || false,
            created_at: new Date(),
            updated_at: new Date(),
            // Champs optionnels avec valeurs par défaut
            advertising_id: null,
            lat: null,
            lng: null,
            club_ids: [],
            equipment_club_ids: [],
        };

        // Mapper vers un Parrain et l'ajouter au store
        const newParrain = this.partenaireMapper.mapToParrain(newPartenaire);

        this.parrains.update((currentParrains) => {
            // Vérifier la limite de sponsors principaux
            if (newParrain.isMainSponsor) {
                const currentMainSponsorsCount = currentParrains.filter(
                    (p) => p.isMainSponsor
                ).length;
                if (currentMainSponsorsCount >= 5) {
                    console.warn('❌ Limite de 5 sponsors principaux atteinte');
                    newParrain.isMainSponsor = false;
                }
            }

            const updatedParrains = [...currentParrains, newParrain];


            return updatedParrains;
        });
    }

    // Mettre à jour un sponsor existant depuis le formulaire
    updateSponsorFromForm(
        partenaireData: Partial<Partenaire> & { id: string }
    ): void {


        this.parrains.update((currentParrains) => {
            const existingIndex = currentParrains.findIndex(
                (p) => p.id === partenaireData.id
            );

            if (existingIndex >= 0) {
                // Créer un objet Partenaire complet pour le mapping
                const existingParrain = currentParrains[existingIndex];
                const updatedPartenaire: Partenaire = {
                    id: partenaireData.id,
                    name: partenaireData.name || existingParrain.name,
                    email: partenaireData.email || '',
                    phone_number:
                        partenaireData.phone_number ||
                        existingParrain.phoneNumber,
                    contact_name: partenaireData.contact_name || '',
                    street: partenaireData.street || existingParrain.street,
                    zipcode: partenaireData.zipcode || existingParrain.zipcode,
                    city: partenaireData.city || existingParrain.city,
                    country: partenaireData.country || existingParrain.country,
                    website_url:
                        partenaireData.website_url ||
                        existingParrain.urlWebSite,
                    logo_sponsor: partenaireData.logo_sponsor || '',
                    banner_sponsor: partenaireData.banner_sponsor || '',
                    description: partenaireData.description || '',
                    is_main_sponsor:
                        partenaireData.is_main_sponsor ??
                        existingParrain.isMainSponsor,
                    updated_at: new Date(),
                    // Garder les valeurs existantes pour les champs non modifiés
                    created_at: new Date(),
                    advertising_id: null,
                    lat: null,
                    lng: null,
                    club_ids: [],
                    equipment_club_ids: [],
                };

                // Vérifier la limite si on promeut en sponsor principal
                if (
                    updatedPartenaire.is_main_sponsor &&
                    !existingParrain.isMainSponsor
                ) {
                    const currentMainSponsorsCount = currentParrains.filter(
                        (p) => p.isMainSponsor
                    ).length;
                    if (currentMainSponsorsCount >= 5) {
                        console.warn(
                            '❌ Limite de 5 sponsors principaux atteinte'
                        );
                        updatedPartenaire.is_main_sponsor = false;
                    }
                }

                const updatedParrain =
                    this.partenaireMapper.mapToParrain(updatedPartenaire);
                const updatedParrains = [...currentParrains];
                updatedParrains[existingIndex] = updatedParrain;


                return updatedParrains;
            } else {
                console.warn(
                    '❌ Sponsor à mettre à jour non trouvé:',
                    partenaireData.id
                );
                return currentParrains;
            }
        });
    }

    // Supprimer un sponsor du store
    removeSponsor(sponsorId: string): void {
        
        this.parrains.update((currentParrains) => {
            const filteredParrains = currentParrains.filter(p => p.id !== sponsorId);
            
            return filteredParrains;
        });
    }

    // Ajouter une méthode pour obtenir un parrain de fallback depuis la config si nécessaire
    getFallbackParrainFromConfig(): Parrain | null {
        if (parrainConfig && parrainConfig.length > 0) {
            return parrainConfig[0];
        }
        return null;
    }

    // Personnaliser spécifiquement un parrain
    customizeParrain(
        parrainId: string,
        customizations: Partial<Parrain>
    ): void {
        this.parrains.update((currentParrains) => {
            const existingIndex = currentParrains.findIndex(
                (p) => p.id === parrainId
            );

            if (existingIndex >= 0) {
                // Vérifier la limite de sponsors principaux si on essaie de promouvoir
                if (
                    customizations.isMainSponsor &&
                    !currentParrains[existingIndex].isMainSponsor
                ) {
                    const currentMainSponsorsCount = currentParrains.filter(
                        (p) => p.isMainSponsor
                    ).length;
                    if (currentMainSponsorsCount >= 5) {
                        console.warn(
                            'Impossible de promouvoir : limite de 5 sponsors principaux atteinte'
                        );
                        delete customizations.isMainSponsor;
                    }
                }

                currentParrains[existingIndex] = {
                    ...currentParrains[existingIndex],
                    ...customizations,
                    // Fusion profonde pour les objets imbriqués
                    styles: customizations.styles
                        ? {
                              ...currentParrains[existingIndex].styles,
                              ...customizations.styles,
                          }
                        : currentParrains[existingIndex].styles,
                    customize: customizations.customize
                        ? {
                              ...currentParrains[existingIndex].customize,
                              ...customizations.customize,
                          }
                        : currentParrains[existingIndex].customize,
                };
            }

            return [...currentParrains];
        });
    }
}
